import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = ["form", "results", "selectedIds", "unSelectedIds", "selectAll", "loadMore"]

  // ──────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────

  connect() {
    this.lastClickedMovementCheckbox = null
    this.lastMovementRangeAnchorState = null
    this.currentHoveredMovementCheckbox = null

    // Click capture — intercepts shift-click on checkboxes for range selection.
    this.boundMovementSelectClickCapture = this.onMovementSelectClickCapture.bind(this)
    this.element.addEventListener("click", this.boundMovementSelectClickCapture, true)

    // Mouse tracking — powers the shift-hover range preview.
    this.boundMovementSelectMouseOver = this.onMovementSelectMouseOver.bind(this)
    this.boundMovementSelectMouseLeave = this.onMovementSelectMouseLeave.bind(this)
    this.element.addEventListener("mouseover", this.boundMovementSelectMouseOver)
    this.element.addEventListener("mouseleave", this.boundMovementSelectMouseLeave)

    // Shift key tracking — toggles the hover range preview on/off.
    this.boundShiftKeyDown = this.onShiftKeyDown.bind(this)
    this.boundShiftKeyUp = this.onShiftKeyUp.bind(this)
    document.addEventListener("keydown", this.boundShiftKeyDown)
    document.addEventListener("keyup", this.boundShiftKeyUp)

    // External event — clears selection when another controller signals a reset.
    this.boundDeselectAll = this.deselectAllFromDocumentEvent.bind(this)
    document.addEventListener("search:deselectAll", this.boundDeselectAll)
  }

  disconnect() {
    this.element.removeEventListener("click", this.boundMovementSelectClickCapture, true)
    this.element.removeEventListener("mouseover", this.boundMovementSelectMouseOver)
    this.element.removeEventListener("mouseleave", this.boundMovementSelectMouseLeave)
    document.removeEventListener("keydown", this.boundShiftKeyDown)
    document.removeEventListener("keyup", this.boundShiftKeyUp)
    document.removeEventListener("search:deselectAll", this.boundDeselectAll)
    this.clearMovementRangePreview()
  }

  // ──────────────────────────────────────────────────────────
  // Stimulus actions — invoked from data-action in templates
  // ──────────────────────────────────────────────────────────

  // Single checkbox toggle, driven by a data-action on the input.
  select = (event) => {
    let ids = this.selectedIdsTarget.value.split(",").filter((id) => id != "")

    if (event.target.checked) {
      ids.push(event.target.value)
    } else {
      ids = ids.filter((id) => id != event.target.value)
    }

    this.selectedIdsTarget.value = ids.join(",")
    this.updateURL()
    this.updateSelectPopup()
  }

  // Fired by change->selection#movementCheckboxChanged on the results container.
  movementCheckboxChanged(event) {
    if (!this.hasResultsTarget || !this.hasSelectedIdsTarget) return
    const cb = event.target
    if (!cb.matches?.('input[type="checkbox"][name^="movement_select_"]')) return
    if (cb.name === "movement_select_all") return
    if (!this.resultsTarget.contains(cb)) return

    this.lastClickedMovementCheckbox = cb
    this.lastMovementRangeAnchorState = cb.checked

    this.syncMovementSelection()
    this.updateURL()
    this.updateSelectPopup()
  }

  selectAll = () => {
    this.selectedIdsTarget.value = null
    this.unSelectedIdsTarget.value = null
    this.selectAllTarget.value = "1"
    this.element.querySelectorAll('.table--standard input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = true
    })
    this.updateURL()
    this.updateLoadMoreURL()
    this.updateSelectPopup()
  }

  selectAllToggle() {
    if (this.selectAllTarget.value == "1") {
      this.deselectAll()
    } else {
      this.selectAll()
    }
  }

  deselectAll() {
    this.lastClickedMovementCheckbox = null
    this.lastMovementRangeAnchorState = null
    this.selectedIdsTarget.value = null
    this.unSelectedIdsTarget.value = null
    this.selectAllTarget.value = null
    this.element.querySelectorAll('.table--standard input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = false
    })
    this.updateURL()
    this.updateLoadMoreURL()
    this.updateSelectPopup()
  }

  bulkReconcileMovements(event) {
    const params = this.paramsFromSearchForm()
    const selected = params.get("selected_ids")
    if (!selected) return

    const clientId = location.pathname.split("/")[2]
    const poolerId = location.pathname.split("/")[4]
    const url = `${location.origin}/clients/${clientId}/poolers/${poolerId}/movements/bulk_reconcile?${params.toString()}`
    const frame = document.querySelector("turbo-frame#modal")
    if (frame) frame.src = url
    document.dispatchEvent(new CustomEvent("modal:show"))
  }

  async bulkDeleteMovements(event) {
    this.resultsTarget.classList.add("loading")
    try {
      const params = this.paramsFromSearchForm()
      const token = document.querySelector('meta[name="csrf-token"]').content
      const clientId = location.pathname.split("/")[2]
      const poolerId = location.pathname.split("/")[4]
      const response = await fetch(`${location.origin}/clients/${clientId}/poolers/${poolerId}/movements/bulk_destroy`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      })
      if (!response.ok) throw new Error("Delete failed")

      const html = await response.text()
      if (html) Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error("Delete failed:", error)
    } finally {
      this.resultsTarget.classList.remove("loading")
    }
  }

  async downloadExport(event) {
    const button = event.currentTarget
    button.disabled = true
    button.classList.add("loading")
    this.resultsTarget.classList.add("loading")
    let openedModal = false
    try {
      const params = this.paramsFromSearchForm()
      const token = document.querySelector('meta[name="csrf-token"]').content
      const clientId = location.pathname.split("/")[2]
      const poolerId = location.pathname.split("/")[4]
      const response = await fetch(`${location.origin}/clients/${clientId}/poolers/${poolerId}/exports`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
      })

      if (!response.ok) throw new Error("Export failed")

      const contentType = response.headers.get("Content-Type") || ""
      if (contentType.includes("text/vnd.turbo-stream.html")) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        openedModal = true
        return
      }

      const contentDisposition = response.headers.get("Content-Disposition")
      const filename = contentDisposition
        ? contentDisposition.split(";")[1].split("filename=")[1].replace(/["']/g, "")
        : "export.xlsx"

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Export failed:", error)
    } finally {
      this.resultsTarget.classList.remove("loading")
      button.disabled = false
      button.classList.remove("loading")
    }

    if (openedModal) return

    this.deselectAll()
    document.dispatchEvent(new CustomEvent("search:refresh"))
  }

  // ──────────────────────────────────────────────────────────
  // Document events
  // ──────────────────────────────────────────────────────────

  // Clears selection state without fetching /select_popup. Callers of the
  // "search:deselectAll" document event also dispatch "search:refresh"
  // immediately after, and that refresh re-renders the popup as part of the
  // form submit — so fetching it here would be a duplicate request.
  deselectAllFromDocumentEvent() {
    if (!this.hasSelectedIdsTarget) return
    this.lastClickedMovementCheckbox = null
    this.lastMovementRangeAnchorState = null
    this.selectedIdsTarget.value = null
    this.unSelectedIdsTarget.value = null
    this.selectAllTarget.value = null
    this.element.querySelectorAll('.table--standard input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = false
    })
    this.updateURL()
    this.updateLoadMoreURL()
  }

  // ──────────────────────────────────────────────────────────
  // Shift-click range selection + hover preview
  // ──────────────────────────────────────────────────────────
  // Click runs in capture phase so we can preventDefault before the browser
  // toggles the checkbox. Hover/keyboard handlers paint a preview of the rows
  // a shift-click would affect, using the same CSS class as :hover.

  onMovementSelectClickCapture = (event) => {
    if (!this.hasResultsTarget || !this.hasSelectedIdsTarget) return
    const cb = this.movementCheckboxFromEventTarget(event.target)
    if (!cb || !this.resultsTarget.contains(cb)) return

    const last = this.lastClickedMovementCheckbox
    const lastOk = last?.isConnected && this.resultsTarget.contains(last)

    if (event.shiftKey && lastOk) {
      const boxes = this.movementSelectCheckboxes()
      const start = this.movementCheckboxIndex(boxes, last)
      const end = this.movementCheckboxIndex(boxes, cb)
      if (start >= 0 && end >= 0 && start !== end) {
        event.preventDefault()
        event.stopPropagation()
        const lo = Math.min(start, end)
        const hi = Math.max(start, end)
        const on = this.lastMovementRangeAnchorState ?? last.checked
        for (let i = lo; i <= hi; i++) boxes[i].checked = on
        this.syncMovementSelection()
        this.updateURL()
        this.updateSelectPopup()
        this.lastClickedMovementCheckbox = cb
        this.lastMovementRangeAnchorState = on
        this.clearMovementRangePreview()
        return
      }
    }

    this.lastClickedMovementCheckbox = cb
    this.clearMovementRangePreview()
  }

  onMovementSelectMouseOver(event) {
    if (!this.hasResultsTarget) return
    const cb = this.movementCheckboxFromEventTarget(event.target)
    if (!cb || !this.resultsTarget.contains(cb)) return
    if (cb.name === "movement_select_all") return
    this.currentHoveredMovementCheckbox = cb
    if (event.shiftKey) this.renderMovementRangePreview(cb)
  }

  onMovementSelectMouseLeave() {
    this.currentHoveredMovementCheckbox = null
    this.clearMovementRangePreview()
  }

  onShiftKeyDown(event) {
    if (event.key !== "Shift" || event.repeat) return
    if (this.currentHoveredMovementCheckbox) {
      this.renderMovementRangePreview(this.currentHoveredMovementCheckbox)
    }
  }

  onShiftKeyUp(event) {
    if (event.key !== "Shift") return
    this.clearMovementRangePreview()
  }

  renderMovementRangePreview(cb) {
    if (!this.hasResultsTarget) return
    const last = this.lastClickedMovementCheckbox
    if (!last?.isConnected || !this.resultsTarget.contains(last)) return
    const boxes = this.movementSelectCheckboxes()
    const start = this.movementCheckboxIndex(boxes, last)
    const end = this.movementCheckboxIndex(boxes, cb)
    if (start < 0 || end < 0) return
    this.clearMovementRangePreview()
    const lo = Math.min(start, end)
    const hi = Math.max(start, end)
    for (let i = lo; i <= hi; i++) {
      const row = boxes[i].closest(".table--row")
      if (row) row.classList.add("table--row--range-preview")
    }
  }

  clearMovementRangePreview() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.querySelectorAll(".table--row--range-preview").forEach((row) => {
      row.classList.remove("table--row--range-preview")
    })
  }

  // ──────────────────────────────────────────────────────────
  // Checkbox utilities — DOM queries and selection state sync
  // ──────────────────────────────────────────────────────────

  movementSelectCheckboxes() {
    if (!this.hasResultsTarget) return []
    return Array.from(
      this.resultsTarget.querySelectorAll(
        'input[type="checkbox"][name^="movement_select_"]'
      )
    )
  }

  movementCheckboxIndex(boxes, el) {
    if (!el) return -1
    let i = boxes.indexOf(el)
    if (i >= 0) return i
    const v = el.value
    if (v === undefined || v === "") return -1
    return boxes.findIndex((b) => b.value === v)
  }

  movementCheckboxFromEventTarget(target) {
    if (!target?.closest) return null
    const input = target.closest('input[type="checkbox"][name^="movement_select_"]')
    if (input) return input
    const label = target.closest("label")
    const c = label?.control
    return c?.matches?.("input[type='checkbox'][name^='movement_select_']") ? c : null
  }

  // Recompute selectedIds from the current DOM checkbox states.
  syncMovementSelection() {
    if (!this.hasResultsTarget || !this.hasSelectedIdsTarget) return
    const boxes = this.movementSelectCheckboxes()
    const idSet = new Set(boxes.filter((b) => b.checked).map((b) => String(b.value)))
    this.selectedIdsTarget.value = [...idSet].join(",")
    for (const box of boxes) box.checked = idSet.has(String(box.value))
  }

  // Restore checkbox states from the hidden selection fields after a re-render
  // (e.g. when the select popup turbo stream replaces part of the DOM).
  reapplyMovementSelectionFromHidden() {
    if (!this.hasResultsTarget || !this.hasSelectedIdsTarget) return
    const selectAllMode = this.hasSelectAllTarget && this.selectAllTarget.value === "1"
    const selectedIds = new Set(
      (this.selectedIdsTarget.value || "").split(",").map((s) => s.trim()).filter(Boolean)
    )
    const unselectedIds = new Set(
      this.hasUnSelectedIdsTarget
        ? (this.unSelectedIdsTarget.value || "").split(",").map((s) => s.trim()).filter(Boolean)
        : []
    )
    for (const box of this.movementSelectCheckboxes()) {
      const id = String(box.value)
      box.checked = selectAllMode ? !unselectedIds.has(id) : selectedIds.has(id)
    }
  }

  // ──────────────────────────────────────────────────────────
  // Search form + URL state
  // ──────────────────────────────────────────────────────────

  paramsFromSearchForm() {
    const params = new URLSearchParams()
    const currentParams = new URLSearchParams(window.location.search)
    if (!this.hasFormTarget) return params
    Array.from(this.formTarget.elements).forEach((element) => {
      if (element.name) {
        if (element.type == "checkbox" || element.type == "radio") {
          if (element.checked) {
            if (element.name.endsWith("[]")) {
              params.append(element.name, element.value)
            } else {
              params.set(element.name, element.value)
            }
          }
        } else if (element.value || ["q", "selected_ids", "unselected_ids", "select_all"].includes(element.name)) {
          params.set(element.name, element.value)
        }
      }
    })
    if (!params.has("tab") && currentParams.has("tab")) {
      params.set("tab", currentParams.get("tab"))
    }
    if (!params.has("direction") && currentParams.has("direction")) {
      params.set("direction", currentParams.get("direction"))
    }
    return params
  }

  updateURL() {
    const params = this.paramsFromSearchForm()
    window.history.pushState(history.state, "", `${location.pathname}?${params}`)
  }

  updateLoadMoreURL() {
    if (this.hasLoadMoreTarget) {
      const url = new URL(this.loadMoreTarget.href, window.location.origin)
      this.loadMoreTarget.href = url.toString()
    }
  }

  async updateSelectPopup() {
    if (!this.hasFormTarget) return
    const params = this.paramsFromSearchForm()
    const response = await fetch(`${location.origin}${location.pathname}/select_popup?${params}`, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
    const html = await response.text()
    Turbo.renderStreamMessage(html)
    this.reapplyMovementSelectionFromHidden()
  }
}
